import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
} from "react";
import { ITenant } from "../interfaces/Tenant";
import { Splash } from "../pages/Splash";
import { loginService } from "../services/LoginService";
import { getTenantConfig } from "../services/TenantServices";

import { getCurrentUser,  getUsersService } from "../services/UserServices";

type ProviderProps = {
  children: ReactNode;
};

type ContextProps = {
  isAuthenticated: boolean;
  tenant?: ITenant;
  isFinancial: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  isCurrentTokenValid: () => Promise<boolean>;
  role: string,
  config: any;
  authenticateUser: ({
    email,
    password,
  }: AuthenticateUserProps) => Promise<Response>;
};

type AuthenticateUserProps = {
  email: string;
  password: string;
};

type Response = {
  status: boolean;
  message?: string;
};

export const AuthContext = createContext({} as ContextProps);

export function AuthProvider({ children }: ProviderProps) {
  const [tenant, setTenant] = useState<ITenant>();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isFinancial, setIsFinancial] = useState<boolean>(false);
  const [role, setRole] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(true);
  const [config, setConfig] = useState<any>(undefined);

  useEffect(() => {
    isCurrentTokenValid();
  }, []);

  function getConfig() {
    const stringConfig = localStorage.getItem("config");
    if (stringConfig) {
      const parsed = JSON.parse(stringConfig);
      setConfig(parsed);
    }
  }

  useEffect(() => {
    if (config?.color) {
      const rgb = config?.color
        .replace(
          /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
          (m: string, r: string, g: string, b: string) =>
            "#" + r + r + g + g + b + b
        )
        .substring(1)
        .match(/.{2}/g)
        .map((x: any) => parseInt(x, 16));

      document?.getElementById("root")?.setAttribute(
        "style",
        `--primary: ${config?.color};
         --darker-primary: rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.1)`
      );
    } else {
      document?.getElementById("root")?.setAttribute(
        "style",
        `--primary: #FCCC34;
         --darker-primary:rgba(208, 255, 1, 0.1)`
      );
    }
  }, [config]);

  async function authenticateUser({
    email,
    password,
  }: AuthenticateUserProps): Promise<Response> {
    return await loginService({ email, password })
      .then(async (response) => {
        if (response.status) {
            localStorage.setItem("token", response.token);
            await getCurrentUser().then(res=>{
            if(res){
              localStorage.setItem("user", JSON.stringify(res));
              setRole(res.role.name.toLowerCase())
            }
            setTenant(response.user.tenant);

            if (response.user.role.name === "financial" || "master") {
              setIsFinancial(true);
            }
            setIsAuthenticated(true);
            })
            await getTenantConfig()
              .then((res) => {
                localStorage.setItem("config", JSON.stringify(res.item));
                setConfig(res.item);
              })
              .catch((err) => {
                return { status: false };
              });
            return { status: true };
        }
        return { status: false };
      })
      .catch(() => {
        return {
          status: false,
          message: "E-mail ou senha incorretos",
        };
      });
  }

  async function isCurrentTokenValid() {
    const isUserAuthenticated = await getCurrentUser()

    if (isUserAuthenticated) {
      setIsAuthenticated(true);
          localStorage.setItem("user", JSON.stringify(isUserAuthenticated));
          await getTenantConfig()
        .then((res) => {
          localStorage.setItem("config", JSON.stringify(res.item));
        })
        .catch((err) => {});
      const stringUser = localStorage.getItem("user");
      if (stringUser) {
        const user = JSON.parse(stringUser);
        setTenant(user.tenant);
        setRole(user.role.name.toLowerCase())
        if (user.role.name.toLowerCase() === 'financial' || user.role.name.toLowerCase() === 'master') {
          setIsFinancial(true);
        }

        getConfig();
      }
      setLoading(false);

      return true;
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("config");
    setIsAuthenticated(false);
    setLoading(false);

    return false;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        isCurrentTokenValid,
        authenticateUser,
        isFinancial,
        config,
        tenant,
        role
      }}
    >
      {loading ? <Splash /> : children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};
